// The paywall (components/modals/UpgradeModal), the locked cover
// (components/studio/LockedCover, LockedVideoPreview) and the bar that
// brings people back to a checkout they left (components/upsell/ReturnBar).
// See messages/README.md for how to add strings.
//
// Prices and plan names arrive already formatted as {price} / {plan}; only
// the sentence around them is translated. {time} is a live countdown
// ("47:12:03") that the code styles, so it stays a placeholder.
export const paywall = {
  en: {
    // What each plan includes (the "What you get with <plan>" list).
    "paywall.hl.unlimitedCovers": "Unlimited covers",
    "paywall.hl.voices": "93,000+ voices",
    "paywall.hl.downloads": "Downloads",
    "paywall.hl.duetsTts": "Duets and text to speech",
    "paywall.hl.trainVoices": "Train your voices",
    "paywall.hl.hdVideos": "1080p videos",
    "paywall.hl.videoCoins": "50 video coins a month",
    "paywall.hl.marbleLyric": "Marble Run and lyric videos",
    "paywall.hl.priority": "Priority processing",
    "paywall.hl.unlimitedCoins": "Unlimited video coins",
    "paywall.hl.apiCredits": "$25 API credits a month",
    "paywall.hl.everythingCreator": "Everything in Creator",
    "paywall.hl.thisCover": "This cover, now",

    // Why the paywall opened: headline and line under it.
    "paywall.reason.unlock.title": "Unlock your cover",
    "paywall.reason.unlock.body":
      "Your voice and audio are set. Subscribe and your cover starts straight away, ready in about a minute. Then make as many as you like.",
    "paywall.reason.create.title": "Your cover is ready to make",
    "paywall.reason.create.body":
      "Your audio and voice are set up. Pick a plan to make it, then make as many covers as you like.",
    "paywall.reason.browse.title": "Make covers with any voice",
    "paywall.reason.browse.body":
      "Unlimited covers, downloads and all 93,000+ voices. Cancel any time.",
    "paywall.reason.credits.title": "You've used your credits",
    "paywall.reason.credits.body":
      "Your next cover is waiting. Basic has no limit, so you never count credits again.",
    "paywall.reason.words.title": "Make the voice say your words",
    "paywall.reason.words.body":
      "Type words or describe a song and the voice sings or says it. Included from Basic.",
    "paywall.reason.video.title": "Turn it into a video",
    "paywall.reason.video.body":
      "Marble Run and lyric videos, ready for TikTok and Reels. Creator includes 50 video coins every month.",
    "paywall.reason.resume.title": "Pick up where you left off",
    "paywall.reason.resume.body":
      "Your plan is saved. Finish checking out, then make as many covers as you like.",
    "paywall.reason.train.title": "Make a voice nobody else has",
    "paywall.reason.train.body":
      "Train your own voices from a few samples and use them in any cover. Unlimited on Creator.",

    // The main button.
    "paywall.cta.unlockFor": "Unlock my cover for {price}",
    "paywall.cta.keepGoingFor": "Keep going for {price}",
    "paywall.cta.backToCreatorFor": "Come back to Creator for {price}",
    "paywall.cta.backFor": "Come back for {price}",
    "paywall.cta.startFor": "Start for {price}",
    "paywall.cta.startPlanFor": "Start {plan} for {price}",

    // Renewal terms next to the pay button (required before payment).
    "paywall.renew.welcome":
      "{price} a month for as long as you stay. Renews monthly until you cancel.",
    "paywall.renew.then":
      "Then {price} a month. Renews monthly until you cancel.",
    "paywall.renew.yearly":
      "{price} billed today for 12 months. Renews yearly until you cancel.",

    "paywall.toast.updated": "Your subscription has been updated!",
    "paywall.toast.error": "Something went wrong, please try again.",

    // Welcome back (their paid plan ended).
    "paywall.welcomeBack": "WELCOME BACK",
    "paywall.priceMonth": "{price} a month",
    "paywall.welcome.offerCreator": "Creator for {price} a month",
    "paywall.welcome.card":
      "Your plan stopped because a payment didn't go through, not because you left. Pick up where you left off for {offer}.",
    "paywall.welcome.price":
      "You told us it cost too much, so here's less: {offer} instead of {was}, for as long as you stay.",
    "paywall.welcome.broken":
      "You told us something wasn't working. In the last week {n} in 100 covers came out fine. Come back for {offer}.",
    "paywall.welcome.secretCreator":
      "Creator for {price} a month on a plan we don't advertise. It stays yours for as long as you're subscribed.",
    "paywall.welcome.secret":
      "Everything you had, for {price} a month on a plan we don't advertise. It stays yours for as long as you're subscribed.",
    "paywall.welcome.coversHere": "Your {n} covers are still here",
    "paywall.welcome.coverHere": "Your cover is still here",
    "paywall.welcome.makeAgain": "Make covers again",
    "paywall.ended.today": "Your plan ended today",
    "paywall.ended.yesterday": "Your plan ended yesterday",
    "paywall.ended.days": "Your plan ended {n} days ago",
    "paywall.ended.months": "Your plan ended {n} months ago",
    "paywall.more": "more",

    // Plan cards and the hero.
    "paywall.recommended": "RECOMMENDED",
    "paywall.perMonth": "a month",
    "paywall.perMonthStay": "a month, for as long as you stay",
    "paywall.firstMonth": "first month",
    "paywall.today": "today",
    "paywall.heldFor": "Held for {time}",
    "paywall.coverReady": "YOUR COVER IS READY TO MAKE",
    "paywall.coverSetUp": "Your audio and voice are set up",
    "paywall.coversToday": "{n} covers made on Jammable today",
    "paywall.coversTodayShort": "{n} covers made today",
    "paywall.pickPlanUnlock": "Pick a plan to unlock it",
    "paywall.pickPlan": "Pick a plan",
    "paywall.whatYouGet": "WHAT YOU GET WITH {plan}",
    "paywall.secure": "Secure checkout with Stripe · cancel any time",
    "paywall.topPlan": "You're on our top plan",
    "paywall.managePlan": "Manage your plan",

    // Checkout step.
    "paywall.changePlan": "Change plan",
    "paywall.planYearly": "{plan}, yearly",
    "paywall.planMonthly": "{plan} plan",
    "paywall.billedYearly": "Billed yearly",
    "paywall.pay": "Pay",
    "paywall.payUnlock": "Pay and unlock",
    "paywall.checkoutLoading": "Getting your checkout ready",

    // Leaving without the cover.
    "paywall.leave.title": "Leave without your cover?",
    "paywall.leave.oneStep": "You're one step away.",
    "paywall.leave.audioIn": "Your audio is in",
    "paywall.leave.voiceReady": "{names} is ready to sing it",
    "paywall.leave.voicesReady": "{names} are ready to sing it",
    "paywall.leave.voicePicked": "Your voice is picked",
    "paywall.leave.makeStep": "Make the cover: about a minute",
    "paywall.leave.gets": "UNLOCKING GETS YOU",
    "paywall.leave.held":
      "It's held for {time}. After that it's gone and you'd have to make it again.",
    "paywall.unlockMyCover": "Unlock my cover",
    "paywall.leaveAnyway": "Leave anyway",

    // The yearly gift on the leave step.
    "paywall.gift.wrapping": "Wrapping something for you…",
    "paywall.gift.beforeYouGo": "BEFORE YOU GO",
    "paywall.gift.saved": "We've saved you a gift",
    "paywall.gift.openToSee": "Open it to see what it is.",
    "paywall.gift.opening": "Opening…",
    "paywall.gift.open": "Open my gift",
    "paywall.gift.noThanks": "No thanks, leave",
    "paywall.gift.yours": "YOUR GIFT",
    "paywall.gift.headline": "{plan} for {price} a month",
    "paywall.gift.body":
      "Unlimited covers with every voice, locked in for 12 months.",
    "paywall.gift.save": "SAVE {pct}%",
    "paywall.perMo": "/mo",
    "paywall.gift.billedYearly": "{price} billed yearly",
    "paywall.gift.lockIn": "Lock in {price} a month",
    "paywall.seeMonthly": "See monthly plans",

    // The locked cover's page.
    "paywall.locked.step.upload": "Uploading your audio",
    "paywall.locked.step.match": "Matching the voice",
    "paywall.locked.step.mix": "Setting up the mix",
    "paywall.locked.yourAudio": "your audio",
    "paywall.locked.yourAiSong": "your AI song",
    "paywall.locked.yourWords": "your words",
    "paywall.locked.yourVoice": "Your voice",
    "paywall.locked.startFailed": "Couldn't start the cover. Try again.",
    "paywall.locked.makeNow": "Make it now",
    "paywall.unlock": "Unlock",
    "paywall.locked.pctDone": "{pct}% done",
    "paywall.locked.gettingReady": "Getting {names} ready for {song}",
    "paywall.locked.done": "done",
    "paywall.locked.now": "now",
    "paywall.locked.expired": "This cover expired",
    "paywall.locked.expiredBody":
      "Locked covers are held for 48 hours. Make it again in a minute.",
    "paywall.locked.makeAgain": "Make it again",
    "paywall.locked.readyToMake": "Ready to make",
    "paywall.locked.readyOne": "{names} AI voice is ready to voice {song}",
    "paywall.locked.readyMany": "{names} AI voices are ready to voice {song}",
    "paywall.locked.onPlan":
      "You're on a plan, so it can start right now. It takes about a minute.",
    "paywall.locked.unlockBody":
      "Unlock it and your cover starts straight away. It takes about a minute, and then it's yours to download and share.",
    "paywall.locked.change": "Change the voice or audio",
    "paywall.locked.notMade": "This cover hasn't been made yet",
    "paywall.locked.makeYourOwn":
      "Make your own with {names} in about a minute.",
    "paywall.locked.makeCover": "Make a cover",

    // The locked video preview. "\n" is a line break.
    "paywall.video.unlock": "Unlock your cover\nand video",
    "paywall.video.howItLooks": "This is how your video will look",
    "paywall.video.drawing": "Drawing your video",
    "paywall.video.aria": "{cta}: your cover and its video",

    // The return bar.
    "paywall.return.firstMonth": "{price} for your first month",
    "paywall.return.waiting": "Your {title} cover is waiting",
    "paywall.return.savedWaiting": "Your saved cover is waiting",
    "paywall.return.trialReady": "Your 7-day free trial is ready",
    "paywall.return.trialDetail":
      "Nothing to pay today, then {price} a month. Cancel any time.",
    "paywall.return.startTrial": "Start my free trial",
    "paywall.return.funds": "Your bank said there wasn't enough on the card.",
    "paywall.return.declined": "Your bank declined it.",
    "paywall.return.cardFailed": "Your card didn't go through",
    "paywall.return.cardDetail":
      "Your card didn't go through. Try Apple Pay, Google Pay or another card · {price}",
    "paywall.return.whyDetail":
      "{why} Try Apple Pay, Google Pay or another card · {price}",
    "paywall.return.tryAnother": "Try another way",
    "paywall.return.confirm": "Your bank wanted a quick confirmation",
    "paywall.return.confirmDetail": "One more step and you're in · {price}",
    "paywall.return.finish": "Finish paying",
    "paywall.return.planHere": "Your {plan} plan is still here",
    "paywall.return.heldMore": "Held for {time} more · {price}",
    "paywall.return.thenRegular": "{price}, then {regular}",
    "paywall.return.cancelAny": "{price}, cancel any time",
    "paywall.return.pickUp": "Pick up where I left off",
    "paywall.return.cardStopped":
      "Your plan stopped when a payment didn't go through",
    "paywall.return.comeBack": "Come back to Jammable",
    "paywall.return.pickUpFor": "Pick up where you left off for {offer}.",
    "paywall.return.secretOffer":
      "Come back for {offer}, on a plan we don't advertise.",
    "paywall.return.hide": "Hide",
  },
  es: {
    "paywall.hl.unlimitedCovers": "Covers ilimitados",
    "paywall.hl.voices": "Más de 93 mil voces",
    "paywall.hl.downloads": "Descargas",
    "paywall.hl.duetsTts": "Duetos y texto a voz",
    "paywall.hl.trainVoices": "Entrena tus voces",
    "paywall.hl.hdVideos": "Videos en 1080p",
    "paywall.hl.videoCoins": "50 monedas de video al mes",
    "paywall.hl.marbleLyric": "Videos Marble Run y con letra",
    "paywall.hl.priority": "Procesamiento prioritario",
    "paywall.hl.unlimitedCoins": "Monedas de video ilimitadas",
    "paywall.hl.apiCredits": "$25 en créditos de API al mes",
    "paywall.hl.everythingCreator": "Todo lo de Creator",
    "paywall.hl.thisCover": "Este cover, ya",

    "paywall.reason.unlock.title": "Desbloquea tu cover",
    "paywall.reason.unlock.body":
      "Tu voz y tu audio están listos. Suscríbete y tu cover empieza al instante; en un minuto, más o menos, está listo. Después, haz todos los que quieras.",
    "paywall.reason.create.title": "Tu cover está listo para crearse",
    "paywall.reason.create.body":
      "Tu audio y tu voz ya están listos. Elige un plan para crearlo y luego haz todos los covers que quieras.",
    "paywall.reason.browse.title": "Haz covers con cualquier voz",
    "paywall.reason.browse.body":
      "Covers ilimitados, descargas y las más de 93 mil voces. Cancela cuando quieras.",
    "paywall.reason.credits.title": "Ya usaste tus créditos",
    "paywall.reason.credits.body":
      "Tu próximo cover te espera. Basic no tiene límite, así que no vuelves a contar créditos.",
    "paywall.reason.words.title": "Haz que la voz diga tus palabras",
    "paywall.reason.words.body":
      "Escribe un texto o describe una canción y la voz lo canta o lo dice. Incluido desde Basic.",
    "paywall.reason.video.title": "Conviértelo en video",
    "paywall.reason.video.body":
      "Videos Marble Run y con letra, listos para TikTok y Reels. Creator incluye 50 monedas de video cada mes.",
    "paywall.reason.resume.title": "Sigue donde lo dejaste",
    "paywall.reason.resume.body":
      "Tu plan está guardado. Termina el pago y haz todos los covers que quieras.",
    "paywall.reason.train.title": "Crea una voz que nadie más tiene",
    "paywall.reason.train.body":
      "Entrena tus propias voces con unas pocas muestras y úsalas en cualquier cover. Ilimitado en Creator.",

    "paywall.cta.unlockFor": "Desbloquear mi cover por {price}",
    "paywall.cta.keepGoingFor": "Continuar por {price}",
    "paywall.cta.backToCreatorFor": "Vuelve a Creator por {price}",
    "paywall.cta.backFor": "Vuelve por {price}",
    "paywall.cta.startFor": "Empieza por {price}",
    "paywall.cta.startPlanFor": "Empieza con {plan} por {price}",

    "paywall.renew.welcome":
      "{price} al mes mientras sigas suscrito. Se renueva cada mes hasta que canceles.",
    "paywall.renew.then":
      "Luego {price} al mes. Se renueva cada mes hasta que canceles.",
    "paywall.renew.yearly":
      "Hoy se cobran {price} por 12 meses. Se renueva cada año hasta que canceles.",

    "paywall.toast.updated": "¡Tu suscripción se actualizó!",
    "paywall.toast.error": "Algo salió mal. Inténtalo de nuevo.",

    "paywall.welcomeBack": "HOLA DE NUEVO",
    "paywall.priceMonth": "{price} al mes",
    "paywall.welcome.offerCreator": "Creator por {price} al mes",
    "paywall.welcome.card":
      "Tu plan se detuvo porque un pago no se procesó, no porque te fueras. Sigue donde lo dejaste por {offer}.",
    "paywall.welcome.price":
      "Nos dijiste que costaba demasiado, así que aquí va menos: {offer} en lugar de {was}, mientras sigas suscrito.",
    "paywall.welcome.broken":
      "Nos dijiste que algo no funcionaba. La última semana, {n} de cada 100 covers salieron bien. Vuelve por {offer}.",
    "paywall.welcome.secretCreator":
      "Creator por {price} al mes, en un plan que no anunciamos. Es tuyo mientras sigas suscrito.",
    "paywall.welcome.secret":
      "Todo lo que tenías, por {price} al mes, en un plan que no anunciamos. Es tuyo mientras sigas suscrito.",
    "paywall.welcome.coversHere": "Tus {n} covers siguen aquí",
    "paywall.welcome.coverHere": "Tu cover sigue aquí",
    "paywall.welcome.makeAgain": "Vuelve a hacer covers",
    "paywall.ended.today": "Tu plan terminó hoy",
    "paywall.ended.yesterday": "Tu plan terminó ayer",
    "paywall.ended.days": "Tu plan terminó hace {n} días",
    "paywall.ended.months": "Tu plan terminó hace {n} meses",
    "paywall.more": "más",

    "paywall.recommended": "RECOMENDADO",
    "paywall.perMonth": "al mes",
    "paywall.perMonthStay": "al mes, mientras sigas suscrito",
    "paywall.firstMonth": "primer mes",
    "paywall.today": "hoy",
    "paywall.heldFor": "Reservado por {time}",
    "paywall.coverReady": "TU COVER ESTÁ LISTO PARA CREARSE",
    "paywall.coverSetUp": "Tu audio y tu voz ya están listos",
    "paywall.coversToday": "{n} covers creados hoy en Jammable",
    "paywall.coversTodayShort": "{n} covers creados hoy",
    "paywall.pickPlanUnlock": "Elige un plan para desbloquearlo",
    "paywall.pickPlan": "Elige un plan",
    "paywall.whatYouGet": "LO QUE OBTIENES CON {plan}",
    "paywall.secure": "Pago seguro con Stripe · cancela cuando quieras",
    "paywall.topPlan": "Ya tienes nuestro plan más alto",
    "paywall.managePlan": "Gestionar tu plan",

    "paywall.changePlan": "Cambiar plan",
    "paywall.planYearly": "{plan}, anual",
    "paywall.planMonthly": "Plan {plan}",
    "paywall.billedYearly": "Facturado al año",
    "paywall.pay": "Pagar",
    "paywall.payUnlock": "Pagar y desbloquear",
    "paywall.checkoutLoading": "Preparando tu pago",

    "paywall.leave.title": "¿Te vas sin tu cover?",
    "paywall.leave.oneStep": "Estás a un paso.",
    "paywall.leave.audioIn": "Tu audio ya está cargado",
    "paywall.leave.voiceReady": "La voz de {names} ya puede cantarlo",
    "paywall.leave.voicesReady": "Las voces de {names} ya pueden cantarlo",
    "paywall.leave.voicePicked": "Tu voz está elegida",
    "paywall.leave.makeStep": "Crear el cover: un minuto, más o menos",
    "paywall.leave.gets": "AL DESBLOQUEAR OBTIENES",
    "paywall.leave.held":
      "Lo guardamos por {time}. Después se borra y tendrías que crearlo otra vez.",
    "paywall.unlockMyCover": "Desbloquear mi cover",
    "paywall.leaveAnyway": "Salir de todos modos",

    "paywall.gift.wrapping": "Preparando algo para ti…",
    "paywall.gift.beforeYouGo": "ANTES DE IRTE",
    "paywall.gift.saved": "Te guardamos un regalo",
    "paywall.gift.openToSee": "Ábrelo para ver qué es.",
    "paywall.gift.opening": "Abriendo…",
    "paywall.gift.open": "Abrir mi regalo",
    "paywall.gift.noThanks": "No, gracias, salir",
    "paywall.gift.yours": "TU REGALO",
    "paywall.gift.headline": "{plan} por {price} al mes",
    "paywall.gift.body":
      "Covers ilimitados con todas las voces, con el precio fijo por 12 meses.",
    "paywall.gift.save": "AHORRA {pct}%",
    "paywall.perMo": "/mes",
    "paywall.gift.billedYearly": "{price} facturados al año",
    "paywall.gift.lockIn": "Asegura {price} al mes",
    "paywall.seeMonthly": "Ver planes mensuales",

    "paywall.locked.step.upload": "Subiendo tu audio",
    "paywall.locked.step.match": "Ajustando la voz",
    "paywall.locked.step.mix": "Preparando la mezcla",
    "paywall.locked.yourAudio": "tu audio",
    "paywall.locked.yourAiSong": "tu canción con IA",
    "paywall.locked.yourWords": "tus palabras",
    "paywall.locked.yourVoice": "Tu voz",
    "paywall.locked.startFailed":
      "No pudimos iniciar el cover. Inténtalo de nuevo.",
    "paywall.locked.makeNow": "Crearlo ahora",
    "paywall.unlock": "Desbloquear",
    "paywall.locked.pctDone": "{pct}% listo",
    "paywall.locked.gettingReady": "Preparando a {names} para {song}",
    "paywall.locked.done": "listo",
    "paywall.locked.now": "ahora",
    "paywall.locked.expired": "Este cover expiró",
    "paywall.locked.expiredBody":
      "Los covers bloqueados se guardan 48 horas. Vuelve a crearlo en un minuto.",
    "paywall.locked.makeAgain": "Crearlo otra vez",
    "paywall.locked.readyToMake": "Listo para crear",
    "paywall.locked.readyOne":
      "La voz IA de {names} está lista para cantar {song}",
    "paywall.locked.readyMany":
      "Las voces IA de {names} están listas para cantar {song}",
    "paywall.locked.onPlan":
      "Ya tienes un plan, así que puede empezar ahora mismo. Tarda un minuto, más o menos.",
    "paywall.locked.unlockBody":
      "Desbloquéalo y tu cover empieza al instante. Tarda un minuto, más o menos, y después es tuyo para descargar y compartir.",
    "paywall.locked.change": "Cambiar la voz o el audio",
    "paywall.locked.notMade": "Este cover aún no se ha creado",
    "paywall.locked.makeYourOwn":
      "Haz el tuyo con {names} en un minuto, más o menos.",
    "paywall.locked.makeCover": "Hacer un cover",

    "paywall.video.unlock": "Desbloquea tu cover\ny tu video",
    "paywall.video.howItLooks": "Así se verá tu video",
    "paywall.video.drawing": "Preparando tu video",
    "paywall.video.aria": "{cta}: tu cover y su video",

    "paywall.return.firstMonth": "{price} tu primer mes",
    "paywall.return.waiting": "Tu cover de {title} te espera",
    "paywall.return.savedWaiting": "Tu cover guardado te espera",
    "paywall.return.trialReady": "Tu prueba gratis de 7 días está lista",
    "paywall.return.trialDetail":
      "Nada que pagar hoy, luego {price} al mes. Cancela cuando quieras.",
    "paywall.return.startTrial": "Empezar mi prueba gratis",
    "paywall.return.funds":
      "Tu banco dijo que no había saldo suficiente en la tarjeta.",
    "paywall.return.declined": "Tu banco la rechazó.",
    "paywall.return.cardFailed": "Tu tarjeta no pasó",
    "paywall.return.cardDetail":
      "Tu tarjeta no pasó. Prueba con Apple Pay, Google Pay u otra tarjeta · {price}",
    "paywall.return.whyDetail":
      "{why} Prueba con Apple Pay, Google Pay u otra tarjeta · {price}",
    "paywall.return.tryAnother": "Probar de otra forma",
    "paywall.return.confirm": "Tu banco pidió una confirmación rápida",
    "paywall.return.confirmDetail": "Un paso más y listo · {price}",
    "paywall.return.finish": "Terminar el pago",
    "paywall.return.planHere": "Tu plan {plan} sigue aquí",
    "paywall.return.heldMore": "Reservado por {time} más · {price}",
    "paywall.return.thenRegular": "{price}, luego {regular}",
    "paywall.return.cancelAny": "{price}, cancela cuando quieras",
    "paywall.return.pickUp": "Seguir donde lo dejé",
    "paywall.return.cardStopped":
      "Tu plan se detuvo cuando un pago no se procesó",
    "paywall.return.comeBack": "Vuelve a Jammable",
    "paywall.return.pickUpFor": "Sigue donde lo dejaste por {offer}.",
    "paywall.return.secretOffer":
      "Vuelve por {offer}, en un plan que no anunciamos.",
    "paywall.return.hide": "Ocultar",
  },
  pt: {
    "paywall.hl.unlimitedCovers": "Covers ilimitados",
    "paywall.hl.voices": "Mais de 93 mil vozes",
    "paywall.hl.downloads": "Downloads",
    "paywall.hl.duetsTts": "Duetos e texto para fala",
    "paywall.hl.trainVoices": "Treine suas vozes",
    "paywall.hl.hdVideos": "Vídeos em 1080p",
    "paywall.hl.videoCoins": "50 moedas de vídeo por mês",
    "paywall.hl.marbleLyric": "Vídeos Marble Run e com letra",
    "paywall.hl.priority": "Processamento prioritário",
    "paywall.hl.unlimitedCoins": "Moedas de vídeo ilimitadas",
    "paywall.hl.apiCredits": "$25 em créditos de API por mês",
    "paywall.hl.everythingCreator": "Tudo do Creator",
    "paywall.hl.thisCover": "Este cover, agora",

    "paywall.reason.unlock.title": "Desbloqueie seu cover",
    "paywall.reason.unlock.body":
      "Sua voz e seu áudio estão prontos. Assine e seu cover começa na hora, pronto em cerca de um minuto. Depois, faça quantos quiser.",
    "paywall.reason.create.title": "Seu cover está pronto para ser feito",
    "paywall.reason.create.body":
      "Seu áudio e sua voz estão prontos. Escolha um plano para fazer o cover e depois faça quantos quiser.",
    "paywall.reason.browse.title": "Faça covers com qualquer voz",
    "paywall.reason.browse.body":
      "Covers ilimitados, downloads e todas as mais de 93 mil vozes. Cancele quando quiser.",
    "paywall.reason.credits.title": "Você usou seus créditos",
    "paywall.reason.credits.body":
      "Seu próximo cover está esperando. O Basic não tem limite, então você nunca mais conta créditos.",
    "paywall.reason.words.title": "Faça a voz dizer suas palavras",
    "paywall.reason.words.body":
      "Digite um texto ou descreva uma música e a voz canta ou fala. Incluído a partir do Basic.",
    "paywall.reason.video.title": "Transforme em vídeo",
    "paywall.reason.video.body":
      "Vídeos Marble Run e com letra, prontos para TikTok e Reels. O Creator inclui 50 moedas de vídeo todo mês.",
    "paywall.reason.resume.title": "Continue de onde parou",
    "paywall.reason.resume.body":
      "Seu plano está salvo. Finalize o pagamento e faça quantos covers quiser.",
    "paywall.reason.train.title": "Crie uma voz que ninguém mais tem",
    "paywall.reason.train.body":
      "Treine suas próprias vozes com poucas amostras e use em qualquer cover. Ilimitado no Creator.",

    "paywall.cta.unlockFor": "Desbloquear meu cover por {price}",
    "paywall.cta.keepGoingFor": "Continuar por {price}",
    "paywall.cta.backToCreatorFor": "Volte para o Creator por {price}",
    "paywall.cta.backFor": "Volte por {price}",
    "paywall.cta.startFor": "Comece por {price}",
    "paywall.cta.startPlanFor": "Comece o {plan} por {price}",

    "paywall.renew.welcome":
      "{price} por mês enquanto você ficar. Renova todo mês até você cancelar.",
    "paywall.renew.then":
      "Depois, {price} por mês. Renova todo mês até você cancelar.",
    "paywall.renew.yearly":
      "{price} cobrados hoje por 12 meses. Renova todo ano até você cancelar.",

    "paywall.toast.updated": "Sua assinatura foi atualizada!",
    "paywall.toast.error": "Algo deu errado. Tente novamente.",

    "paywall.welcomeBack": "QUE BOM TE VER DE NOVO",
    "paywall.priceMonth": "{price} por mês",
    "paywall.welcome.offerCreator": "Creator por {price} por mês",
    "paywall.welcome.card":
      "Seu plano parou porque um pagamento não foi aprovado, não porque você saiu. Continue de onde parou por {offer}.",
    "paywall.welcome.price":
      "Você disse que estava caro demais, então aqui vai menos: {offer} em vez de {was}, enquanto você ficar.",
    "paywall.welcome.broken":
      "Você disse que algo não estava funcionando. Na última semana, {n} de cada 100 covers saíram bem. Volte por {offer}.",
    "paywall.welcome.secretCreator":
      "Creator por {price} por mês, em um plano que não divulgamos. Ele é seu enquanto você continuar assinando.",
    "paywall.welcome.secret":
      "Tudo o que você tinha, por {price} por mês, em um plano que não divulgamos. Ele é seu enquanto você continuar assinando.",
    "paywall.welcome.coversHere": "Seus {n} covers ainda estão aqui",
    "paywall.welcome.coverHere": "Seu cover ainda está aqui",
    "paywall.welcome.makeAgain": "Volte a fazer covers",
    "paywall.ended.today": "Seu plano terminou hoje",
    "paywall.ended.yesterday": "Seu plano terminou ontem",
    "paywall.ended.days": "Seu plano terminou há {n} dias",
    "paywall.ended.months": "Seu plano terminou há {n} meses",
    "paywall.more": "mais",

    "paywall.recommended": "RECOMENDADO",
    "paywall.perMonth": "por mês",
    "paywall.perMonthStay": "por mês, enquanto você ficar",
    "paywall.firstMonth": "primeiro mês",
    "paywall.today": "hoje",
    "paywall.heldFor": "Reservado por {time}",
    "paywall.coverReady": "SEU COVER ESTÁ PRONTO PARA SER FEITO",
    "paywall.coverSetUp": "Seu áudio e sua voz estão prontos",
    "paywall.coversToday": "{n} covers feitos hoje no Jammable",
    "paywall.coversTodayShort": "{n} covers feitos hoje",
    "paywall.pickPlanUnlock": "Escolha um plano para desbloquear",
    "paywall.pickPlan": "Escolha um plano",
    "paywall.whatYouGet": "O QUE VOCÊ GANHA COM O {plan}",
    "paywall.secure": "Pagamento seguro com Stripe · cancele quando quiser",
    "paywall.topPlan": "Você já está no nosso plano mais alto",
    "paywall.managePlan": "Gerenciar seu plano",

    "paywall.changePlan": "Mudar plano",
    "paywall.planYearly": "{plan}, anual",
    "paywall.planMonthly": "Plano {plan}",
    "paywall.billedYearly": "Cobrado anualmente",
    "paywall.pay": "Pagar",
    "paywall.payUnlock": "Pagar e desbloquear",
    "paywall.checkoutLoading": "Preparando seu pagamento",

    "paywall.leave.title": "Vai sair sem seu cover?",
    "paywall.leave.oneStep": "Você está a um passo.",
    "paywall.leave.audioIn": "Seu áudio foi enviado",
    "paywall.leave.voiceReady": "A voz de {names} já pode cantar",
    "paywall.leave.voicesReady": "As vozes de {names} já podem cantar",
    "paywall.leave.voicePicked": "Sua voz foi escolhida",
    "paywall.leave.makeStep": "Fazer o cover: cerca de um minuto",
    "paywall.leave.gets": "AO DESBLOQUEAR VOCÊ GANHA",
    "paywall.leave.held":
      "Ele fica guardado por {time}. Depois disso, some e você teria que fazer de novo.",
    "paywall.unlockMyCover": "Desbloquear meu cover",
    "paywall.leaveAnyway": "Sair mesmo assim",

    "paywall.gift.wrapping": "Embrulhando algo para você…",
    "paywall.gift.beforeYouGo": "ANTES DE SAIR",
    "paywall.gift.saved": "Guardamos um presente para você",
    "paywall.gift.openToSee": "Abra para ver o que é.",
    "paywall.gift.opening": "Abrindo…",
    "paywall.gift.open": "Abrir meu presente",
    "paywall.gift.noThanks": "Não, obrigado, sair",
    "paywall.gift.yours": "SEU PRESENTE",
    "paywall.gift.headline": "{plan} por {price} por mês",
    "paywall.gift.body":
      "Covers ilimitados com todas as vozes, com preço garantido por 12 meses.",
    "paywall.gift.save": "ECONOMIZE {pct}%",
    "paywall.perMo": "/mês",
    "paywall.gift.billedYearly": "{price} cobrados por ano",
    "paywall.gift.lockIn": "Garanta {price} por mês",
    "paywall.seeMonthly": "Ver planos mensais",

    "paywall.locked.step.upload": "Enviando seu áudio",
    "paywall.locked.step.match": "Ajustando a voz",
    "paywall.locked.step.mix": "Preparando a mixagem",
    "paywall.locked.yourAudio": "seu áudio",
    "paywall.locked.yourAiSong": "sua música com IA",
    "paywall.locked.yourWords": "suas palavras",
    "paywall.locked.yourVoice": "Sua voz",
    "paywall.locked.startFailed":
      "Não foi possível iniciar o cover. Tente de novo.",
    "paywall.locked.makeNow": "Fazer agora",
    "paywall.unlock": "Desbloquear",
    "paywall.locked.pctDone": "{pct}% pronto",
    "paywall.locked.gettingReady": "Preparando {names} para {song}",
    "paywall.locked.done": "pronto",
    "paywall.locked.now": "agora",
    "paywall.locked.expired": "Este cover expirou",
    "paywall.locked.expiredBody":
      "Covers bloqueados ficam guardados por 48 horas. Faça de novo em um minuto.",
    "paywall.locked.makeAgain": "Fazer de novo",
    "paywall.locked.readyToMake": "Pronto para fazer",
    "paywall.locked.readyOne":
      "A voz IA de {names} está pronta para cantar {song}",
    "paywall.locked.readyMany":
      "As vozes IA de {names} estão prontas para cantar {song}",
    "paywall.locked.onPlan":
      "Você já tem um plano, então ele pode começar agora. Leva cerca de um minuto.",
    "paywall.locked.unlockBody":
      "Desbloqueie e seu cover começa na hora. Leva cerca de um minuto, e depois é seu para baixar e compartilhar.",
    "paywall.locked.change": "Mudar a voz ou o áudio",
    "paywall.locked.notMade": "Este cover ainda não foi feito",
    "paywall.locked.makeYourOwn":
      "Faça o seu com {names} em cerca de um minuto.",
    "paywall.locked.makeCover": "Fazer um cover",

    "paywall.video.unlock": "Desbloqueie seu cover\ne seu vídeo",
    "paywall.video.howItLooks": "Assim vai ficar seu vídeo",
    "paywall.video.drawing": "Preparando seu vídeo",
    "paywall.video.aria": "{cta}: seu cover e o vídeo dele",

    "paywall.return.firstMonth": "{price} no primeiro mês",
    "paywall.return.waiting": "Seu cover de {title} está esperando",
    "paywall.return.savedWaiting": "Seu cover salvo está esperando",
    "paywall.return.trialReady": "Seu teste grátis de 7 dias está pronto",
    "paywall.return.trialDetail":
      "Nada a pagar hoje, depois {price} por mês. Cancele quando quiser.",
    "paywall.return.startTrial": "Começar meu teste grátis",
    "paywall.return.funds":
      "Seu banco informou que não havia saldo suficiente no cartão.",
    "paywall.return.declined": "Seu banco recusou.",
    "paywall.return.cardFailed": "Seu cartão não passou",
    "paywall.return.cardDetail":
      "Seu cartão não passou. Tente Apple Pay, Google Pay ou outro cartão · {price}",
    "paywall.return.whyDetail":
      "{why} Tente Apple Pay, Google Pay ou outro cartão · {price}",
    "paywall.return.tryAnother": "Tentar de outro jeito",
    "paywall.return.confirm": "Seu banco pediu uma confirmação rápida",
    "paywall.return.confirmDetail": "Mais um passo e pronto · {price}",
    "paywall.return.finish": "Finalizar pagamento",
    "paywall.return.planHere": "Seu plano {plan} ainda está aqui",
    "paywall.return.heldMore": "Reservado por mais {time} · {price}",
    "paywall.return.thenRegular": "{price}, depois {regular}",
    "paywall.return.cancelAny": "{price}, cancele quando quiser",
    "paywall.return.pickUp": "Continuar de onde parei",
    "paywall.return.cardStopped":
      "Seu plano parou quando um pagamento não foi aprovado",
    "paywall.return.comeBack": "Volte para o Jammable",
    "paywall.return.pickUpFor": "Continue de onde parou por {offer}.",
    "paywall.return.secretOffer":
      "Volte por {offer}, em um plano que não divulgamos.",
    "paywall.return.hide": "Ocultar",
  },
};
